import { useRef, useState } from 'react'
import { NodeViewContent, NodeViewWrapper, type NodeViewProps } from '@tiptap/react'
import { useUploader } from '../upload'

/** A byte count as `1.2 MB`, the way a person reads a file size — not the raw
 * number and not always rounded to whole units, which read as 0 KB for
 * anything under a kilobyte. */
function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB']
  let value = bytes / 1024
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit++
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`
}

/**
 * A downloadable attachment — a PDF, a spec, a sample project.
 *
 * Reuses the same uploader every image goes through (`useUploader`): the
 * server already accepts arbitrary file types (see `FigureView`, whose picker
 * takes `application/pdf` too), so nothing on that side has to change for this
 * block to exist. Size is read straight off the browser's `File` object before
 * upload starts — it is not stored on the block, only shown while picking, so
 * this needs no new field on the content model to display it.
 */
export function FileView({ node, updateAttributes, editor }: NodeViewProps) {
  const src = (node.attrs['src'] as string | null) ?? ''
  const name = (node.attrs['name'] as string | null) ?? ''

  const upload = useUploader()
  const picker = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [pickedSize, setPickedSize] = useState<string | null>(null)

  const take = async (file: File | undefined) => {
    if (!file || !upload) return
    setBusy(true)
    try {
      const result = await upload(file)
      if (result) {
        setPickedSize(humanSize(file.size))
        updateAttributes({ src: result.url, ...(name ? {} : { name: file.name }) })
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <NodeViewWrapper className="be-file" data-sls="file">
      <div className="be-file__row" contentEditable={false}>
        <span className="be-file__icon" aria-hidden="true" />
        <input
          className="be-inline-input be-inline-input--title"
          value={name}
          placeholder="Display name, e.g. Sample project.zip"
          disabled={!editor.isEditable}
          onChange={(e) => updateAttributes({ name: e.target.value || null })}
        />
        {pickedSize && <span className="be-file__size">{pickedSize}</span>}
      </div>
      {editor.isEditable && (
        <div className="be-file__fields" contentEditable={false}>
          <input
            className="be-inline-input"
            value={src}
            placeholder="File URL, or choose one below"
            onChange={(e) => updateAttributes({ src: e.target.value || null })}
          />
          {upload && (
            <>
              <button
                type="button"
                className="btn btn--tinted btn--sm"
                disabled={busy}
                onClick={() => picker.current?.click()}
              >
                {busy ? 'Uploading…' : src ? 'Replace file' : 'Choose a file'}
              </button>
              <input
                ref={picker}
                type="file"
                hidden
                onChange={(e) => void take(e.target.files?.[0])}
              />
            </>
          )}
        </div>
      )}
      {!src && (
        <p className="be-file__warn" contentEditable={false}>
          No file yet. This will render as an empty attachment.
        </p>
      )}
      <NodeViewContent className="be-file__caption" />
    </NodeViewWrapper>
  )
}
